import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, Briefcase, Calendar, Check, CheckCircle, Eye, FileText, Mail, Phone, User, X } from 'lucide-react';
import { formatDateDDMonYYYY } from '@/utils/textFormatting';

export function EmploymentComparison({ check, verificationData, setVerificationData, handleFieldVerification }) {
    const candidateData = {
      ...(check?.subcheck_data || {}),
      father_name: check?.father_name || check?.subcheck_data?.father_name || '',
      dob: check?.dob || check?.subcheck_data?.dob || check?.subcheck_data?.date_of_birth || '',
      candidate_name: check?.subcheck_data?.candidate_name || check?.subcheck_data?.name || check?.employee_name || '',
    };
    
    // Verification status options with color codes
    const verificationStatusOptions = [
      { value: 'verified', label: 'Verified', color: 'bg-green-500', textColor: 'text-green-700', bgLight: 'bg-green-50 border-green-300' },
      { value: 'discrepancy', label: 'Discrepancy', color: 'bg-red-500', textColor: 'text-red-700', bgLight: 'bg-red-50 border-red-300' },
      { value: 'partially_verified', label: 'Partially Verified', color: 'bg-sky-400', textColor: 'text-sky-700', bgLight: 'bg-sky-50 border-sky-300' },
      { value: 'not_verified', label: 'Not Verified', color: 'bg-amber-500', textColor: 'text-amber-700', bgLight: 'bg-amber-50 border-amber-300' },
    ];
    
    // Get status color classes
    const getStatusClasses = (status) => {
      const found = verificationStatusOptions.find(o => o.value === status);
      return found || { color: 'bg-gray-300', textColor: 'text-gray-600', bgLight: 'bg-white border-gray-200' };
    };
    
    // Non-verification reasons
    const nonVerificationReasons = [
      { value: 'company_policy', label: 'Company Policy - No disclosure' },
      { value: 'no_records', label: 'No Records Available' },
      { value: 'hr_unavailable', label: 'HR Not Available' },
      { value: 'company_closed', label: 'Company Closed/Merged' },
      { value: 'discrepancy_found', label: 'Discrepancy Found' },
      { value: 'insufficient_info', label: 'Insufficient Information' },
      { value: 'other', label: 'Other' },
    ];
    
    // Clear form function
    const clearEmploymentForm = () => {
      setVerificationData(prev => ({
        ...prev,
        verified_details: {
          ...prev.verified_details,
          company_name: '',
          designation: '',
          designation_status: '',
          designation_reason: '',
          salary: '',
          salary_status: '',
          salary_reason: '',
          rm_name: '',
          rm_status: '',
          rm_reason: '',
          location: '',
          employed_from: '',
          employed_to: '',
          period_status: '',
          period_reason: '',
          reason_for_leaving: '',
          hr_name: '',
          hr_email: '',
          hr_contact: '',
          exit_type: '',
          eligible_for_rehire: '',
          verification_date: '',
          attendance_status: '',
          attendance_rating: '',
          attendance_remarks: '',
          disciplinary_status: '',
          disciplinary_record: '',
          disciplinary_remarks: '',
          relieving_letter_status: '',
          relieving_letter_reason: '',
          payslip_status: '',
          payslip_reason: '',
        }
      }));
      toast.info('Form cleared');
    };
    
    // Render status badge with color
    const renderStatusBadge = (status) => {
      const statusConfig = getStatusClasses(status);
      const statusLabel = verificationStatusOptions.find(o => o.value === status)?.label || 'Pending';
      return (
        <Badge className={`${statusConfig.color} text-white text-xs px-2 py-0.5`}>
          {statusLabel}
        </Badge>
      );
    };
    
    return (
      <div className="space-y-4">
        {/* Header with Clear Button */}
        <div className="flex items-center justify-between bg-gradient-to-r from-slate-800 to-slate-700 text-white px-4 py-3 rounded-lg shadow-lg">
          <h4 className="font-semibold text-base flex items-center gap-2">
            <Briefcase className="w-5 h-5" />
            Employment Verification - {check?.subcheck_type || 'Employment'}
          </h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={clearEmploymentForm}
            className="bg-white/10 hover:bg-white/20 text-white border-white/30 hover:border-white/50"
          >
            <X className="w-4 h-4 mr-1" />
            Clear Form
          </Button>
        </div>
        
        {/* Status Color Legend */}
        <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg border">
          <span className="text-xs font-medium text-slate-600">Status Legend:</span>
          <div className="flex items-center gap-3">
            {verificationStatusOptions.map(opt => (
              <div key={opt.value} className="flex items-center gap-1">
                <div className={`w-3 h-3 rounded-full ${opt.color}`}></div>
                <span className={`text-xs ${opt.textColor}`}>{opt.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <div className="grid grid-cols-2 gap-4">
          {/* LHS - Candidate Provided */}
          <Card className="border-2 border-blue-200 shadow-md bg-gradient-to-b from-blue-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <User className="w-4 h-4" />
                Candidate Provided
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm pt-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Company</span>
                  <span className="font-semibold text-slate-800">{candidateData.employer_name || candidateData.company_name || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Employee ID</span>
                  <span className="font-semibold text-slate-800">{candidateData.employee_code || candidateData.employee_id || '-'}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Designation</span>
                  <span className="font-semibold text-slate-800">{candidateData.designation || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Salary/CTC</span>
                  <span className="font-semibold text-slate-800">{candidateData.salary || candidateData.ctc || '-'}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">From Date</span>
                  <span className="font-semibold text-slate-800">{formatDateDDMonYYYY(candidateData.date_of_joining || candidateData.employed_from || candidateData.start_date) || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">To Date</span>
                  <span className="font-semibold text-slate-800">{candidateData.date_of_relieving || candidateData.employed_to || candidateData.end_date ? formatDateDDMonYYYY(candidateData.date_of_relieving || candidateData.employed_to || candidateData.end_date) : 'Present'}</span>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Reporting Manager</span>
                  <span className="font-semibold text-slate-800">{candidateData.rm_name || candidateData.reporting_manager || '-'}</span>
                </div>
                <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                  <span className="text-blue-600 block text-xs font-medium">Location</span>
                  <span className="font-semibold text-slate-800">{candidateData.location || candidateData.work_location || '-'}</span>
                </div>
              </div>
              <div className="p-2 bg-white rounded-lg border border-blue-100 shadow-sm">
                <span className="text-blue-600 block text-xs font-medium">Reason for Leaving</span>
                <span className="font-semibold text-slate-800">{candidateData.reason_for_leaving || '-'}</span>
              </div>
              
              {/* Documents */}
              <div className="border-t border-blue-100 pt-3 mt-3">
                <span className="text-blue-600 block text-xs font-medium mb-2">Documents Provided</span>
                <div className="flex flex-wrap gap-2">
                  {candidateData.relieving_letter_url && (
                    <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                      <FileText className="w-3 h-3 mr-1" />Relieving Letter
                    </Badge>
                  )}
                  {candidateData.latest_payslip_url && (
                    <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                      <FileText className="w-3 h-3 mr-1" />Payslip
                    </Badge>
                  )}
                  {candidateData.experience_letter_url && (
                    <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                      <FileText className="w-3 h-3 mr-1" />Experience Letter
                    </Badge>
                  )}
                  {!candidateData.relieving_letter_url && !candidateData.latest_payslip_url && !candidateData.experience_letter_url && (
                    <span className="text-slate-400 text-xs italic">No documents provided</span>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* RHS - Verified Details */}
          <Card className="border-2 border-green-200 shadow-md bg-gradient-to-b from-green-50 to-white">
            <CardHeader className="pb-2 bg-gradient-to-r from-green-600 to-green-500 text-white rounded-t-lg">
              <CardTitle className="text-sm flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Verified Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 pt-4">
              {/* Company Verification */}
              <div className="p-2 bg-white rounded-lg border shadow-sm">
                <Label className="text-xs text-slate-600 font-medium">Company Name</Label>
                <Input 
                  value={verificationData.verified_details.company_name || ''} 
                  onChange={(e) => handleFieldVerification('company_name', true, e.target.value)}
                  className="h-8 text-sm mt-1 border-slate-200"
                  placeholder="Enter verified company name"
                />
              </div>
              
              {/* Designation Verification with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.designation_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Designation</Label>
                  <Select 
                    value={verificationData.verified_details.designation_status || ''} 
                    onValueChange={(v) => handleFieldVerification('designation_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.designation || ''} 
                  onChange={(e) => handleFieldVerification('designation', true, e.target.value)}
                  className="h-8 text-sm mb-2 border-slate-200"
                  placeholder="Verified designation"
                />
                {(verificationData.verified_details.designation_status === 'not_verified' || 
                  verificationData.verified_details.designation_status === 'discrepancy') && (
                  <Select 
                    value={verificationData.verified_details.designation_reason || ''} 
                    onValueChange={(v) => handleFieldVerification('designation_reason', true, v)}
                  >
                    <SelectTrigger className="h-7 text-xs border-slate-300">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {nonVerificationReasons.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Salary Verification with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.salary_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Salary/CTC</Label>
                  <Select 
                    value={verificationData.verified_details.salary_status || ''} 
                    onValueChange={(v) => handleFieldVerification('salary_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.salary || ''} 
                  onChange={(e) => handleFieldVerification('salary', true, e.target.value)}
                  className="h-8 text-sm mb-2 border-slate-200"
                  placeholder="Verified salary/CTC"
                />
                {(verificationData.verified_details.salary_status === 'not_verified' || 
                  verificationData.verified_details.salary_status === 'discrepancy') && (
                  <Select 
                    value={verificationData.verified_details.salary_reason || ''} 
                    onValueChange={(v) => handleFieldVerification('salary_reason', true, v)}
                  >
                    <SelectTrigger className="h-7 text-xs border-slate-300">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {nonVerificationReasons.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Reporting Manager Verification with Color */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.rm_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Reporting Manager</Label>
                  <Select 
                    value={verificationData.verified_details.rm_status || ''} 
                    onValueChange={(v) => handleFieldVerification('rm_status', true, v)}
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Input 
                  value={verificationData.verified_details.rm_name || ''} 
                  onChange={(e) => handleFieldVerification('rm_name', true, e.target.value)}
                  className="h-8 text-sm mb-2 border-slate-200"
                  placeholder="Verified RM name"
                />
                {(verificationData.verified_details.rm_status === 'not_verified' || 
                  verificationData.verified_details.rm_status === 'discrepancy') && (
                  <Select 
                    value={verificationData.verified_details.rm_reason || ''} 
                    onValueChange={(v) => handleFieldVerification('rm_reason', true, v)}
                  >
                    <SelectTrigger className="h-7 text-xs border-slate-300">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {nonVerificationReasons.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Employment Period with Verification Status */}
              <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${getStatusClasses(verificationData.verified_details.period_status).bgLight}`}>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-slate-700">Period of Employment</Label>
                  <Select 
                    value={verificationData.verified_details.period_status || ''} 
                    onValueChange={(v) => handleFieldVerification('period_status', true, v)}
                    data-testid="period-status-select"
                  >
                    <SelectTrigger className="h-7 w-36 text-xs border-slate-300" data-testid="period-status-trigger">
                      <SelectValue placeholder="Select Status" />
                    </SelectTrigger>
                    <SelectContent>
                      {verificationStatusOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>
                          <div className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                            {o.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-2 mb-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">From Date</Label>
                    <Input 
                      type="date"
                      data-testid="employed-from-input"
                      value={verificationData.verified_details.employed_from || ''} 
                      onChange={(e) => {
                        handleFieldVerification('employed_from', true, e.target.value);
                        // Alert if date changed from original (pre-filled) value
                        const original = verificationData.verified_details._original_employed_from || '';
                        if (original && e.target.value !== original && verificationData.verified_details.period_status !== 'discrepancy') {
                          toast.warning('From Date changed from employee form. Consider updating Period status to "Discrepancy".');
                        }
                      }}
                      className="h-7 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">To Date</Label>
                    <Input 
                      type="date"
                      data-testid="employed-to-input"
                      value={verificationData.verified_details.employed_to || ''} 
                      onChange={(e) => {
                        handleFieldVerification('employed_to', true, e.target.value);
                        const original = verificationData.verified_details._original_employed_to || '';
                        if (original && e.target.value !== original && verificationData.verified_details.period_status !== 'discrepancy') {
                          toast.warning('To Date changed from employee form. Consider updating Period status to "Discrepancy".');
                        }
                      }}
                      className="h-7 text-sm"
                    />
                  </div>
                </div>
                {(verificationData.verified_details.period_status === 'not_verified' || 
                  verificationData.verified_details.period_status === 'discrepancy') && (
                  <Select 
                    value={verificationData.verified_details.period_reason || ''} 
                    onValueChange={(v) => handleFieldVerification('period_reason', true, v)}
                    data-testid="period-reason-select"
                  >
                    <SelectTrigger className="h-7 text-xs border-slate-300" data-testid="period-reason-trigger">
                      <SelectValue placeholder="Select reason" />
                    </SelectTrigger>
                    <SelectContent>
                      {nonVerificationReasons.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              
              {/* Location - moved below Employment Period */}
              <div className="p-2 bg-white rounded-lg border shadow-sm">
                <Label className="text-xs text-slate-600 font-medium">Location</Label>
                <Input 
                  value={verificationData.verified_details.location || ''} 
                  onChange={(e) => handleFieldVerification('location', true, e.target.value)}
                  className="h-7 text-sm"
                  placeholder="Work location"
                  data-testid="location-input"
                />
              </div>
            </CardContent>
          </Card>
        </div>
        
        {/* Document Verification Section */}
        <Card className="border-2 border-purple-200 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Document Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-4 bg-gradient-to-b from-purple-50 to-white">
            {/* Relieving Letter */}
            <div className="p-3 bg-white rounded-lg border-2 border-purple-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-purple-500" />Relieving Letter
                </Label>
                <Select 
                  value={verificationData.verified_details.relieving_letter_status || ''} 
                  onValueChange={(v) => handleFieldVerification('relieving_letter_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-36 text-xs border-purple-200">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="genuine"><span className="text-green-600 font-medium">✓ Genuine</span></SelectItem>
                    <SelectItem value="fake"><span className="text-red-600 font-medium">✗ Fake/Forged</span></SelectItem>
                    <SelectItem value="not_provided"><span className="text-amber-600">Not Provided</span></SelectItem>
                    <SelectItem value="unable_to_verify"><span className="text-slate-500">Unable to Verify</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(verificationData.verified_details.relieving_letter_status === 'fake' || 
                verificationData.verified_details.relieving_letter_status === 'unable_to_verify') && (
                <Select 
                  value={verificationData.verified_details.relieving_letter_reason || ''} 
                  onValueChange={(v) => handleFieldVerification('relieving_letter_reason', true, v)}
                >
                  <SelectTrigger className="h-7 text-xs border-slate-300"><SelectValue placeholder="Select Reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="signature_mismatch">Signature Mismatch</SelectItem>
                    <SelectItem value="date_discrepancy">Date Discrepancy</SelectItem>
                    <SelectItem value="format_irregular">Format Irregular</SelectItem>
                    <SelectItem value="company_denied">Company Denied Issuance</SelectItem>
                    <SelectItem value="tampered">Tampered/Modified</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {candidateData.relieving_letter_url && (
                <Button variant="outline" size="sm" className="h-7 text-xs mt-2 w-full border-purple-300 text-purple-700 hover:bg-purple-50" 
                  onClick={() => window.open(candidateData.relieving_letter_url, '_blank')}>
                  <Eye className="w-3 h-3 mr-1" /> View Document
                </Button>
              )}
            </div>
            
            {/* Payslip */}
            <div className="p-3 bg-white rounded-lg border-2 border-purple-100 shadow-sm">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <FileText className="w-3 h-3 text-purple-500" />Payslip
                </Label>
                <Select 
                  value={verificationData.verified_details.payslip_status || ''} 
                  onValueChange={(v) => handleFieldVerification('payslip_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-36 text-xs border-purple-200">
                    <SelectValue placeholder="Select Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="genuine"><span className="text-green-600 font-medium">✓ Genuine</span></SelectItem>
                    <SelectItem value="fake"><span className="text-red-600 font-medium">✗ Fake/Forged</span></SelectItem>
                    <SelectItem value="not_provided"><span className="text-amber-600">Not Provided</span></SelectItem>
                    <SelectItem value="unable_to_verify"><span className="text-slate-500">Unable to Verify</span></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {(verificationData.verified_details.payslip_status === 'fake' || 
                verificationData.verified_details.payslip_status === 'unable_to_verify') && (
                <Select 
                  value={verificationData.verified_details.payslip_reason || ''} 
                  onValueChange={(v) => handleFieldVerification('payslip_reason', true, v)}
                >
                  <SelectTrigger className="h-7 text-xs border-slate-300"><SelectValue placeholder="Select Reason" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="amount_mismatch">Amount Mismatch</SelectItem>
                    <SelectItem value="format_irregular">Format Irregular</SelectItem>
                    <SelectItem value="company_denied">Company Denied</SelectItem>
                    <SelectItem value="tampered">Tampered/Modified</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {candidateData.latest_payslip_url && (
                <Button variant="outline" size="sm" className="h-7 text-xs mt-2 w-full border-purple-300 text-purple-700 hover:bg-purple-50" 
                  onClick={() => window.open(candidateData.latest_payslip_url, '_blank')}>
                  <Eye className="w-3 h-3 mr-1" /> View Document
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
        
        {/* HR Verification Section */}
        <Card className="border-2 border-indigo-200 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-indigo-600 to-indigo-500 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <Mail className="w-4 h-4" />
              HR Verification Details
              {(candidateData.hr_email || check?.subcheck_hr_email) && (
                <Badge variant="outline" className="ml-2 text-xs bg-white/20 text-white border-white/30">Auto-filled</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-3 gap-4 pt-4 bg-gradient-to-b from-indigo-50 to-white">
            {/* HR Contact Details */}
            <div className="p-3 bg-white rounded-lg border-2 border-indigo-100 shadow-sm">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <User className="w-3 h-3 text-indigo-500" />HR Name
              </Label>
              <Input 
                value={verificationData.verified_details.hr_name || ''} 
                onChange={(e) => handleFieldVerification('hr_name', true, e.target.value)}
                className="h-8 text-sm border-indigo-200"
                placeholder="HR representative name"
              />
            </div>
            <div className="p-3 bg-white rounded-lg border-2 border-indigo-100 shadow-sm">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <Mail className="w-3 h-3 text-indigo-500" />HR Email
              </Label>
              <Input 
                value={verificationData.verified_details.hr_email || candidateData.hr_email || check?.subcheck_hr_email || ''} 
                onChange={(e) => handleFieldVerification('hr_email', true, e.target.value)}
                className="h-8 text-sm border-indigo-200"
                placeholder="hr@company.com"
              />
            </div>
            <div className="p-3 bg-white rounded-lg border-2 border-indigo-100 shadow-sm">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <Phone className="w-3 h-3 text-indigo-500" />HR Contact
              </Label>
              <Input 
                value={verificationData.verified_details.hr_contact || ''} 
                onChange={(e) => handleFieldVerification('hr_contact', true, e.target.value)}
                className="h-8 text-sm border-indigo-200"
                placeholder="Phone number"
              />
            </div>
            
            {/* Exit Details */}
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.exit_type === 'termination' || verificationData.verified_details.exit_type === 'absconding'
                ? 'bg-red-50 border-red-200' 
                : verificationData.verified_details.exit_type === 'resignation' || verificationData.verified_details.exit_type === 'mutual_separation'
                  ? 'bg-green-50 border-green-200'
                  : 'bg-white border-indigo-100'
            }`}>
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <Briefcase className="w-3 h-3 text-indigo-500" />Exit Type
              </Label>
              <Select 
                value={verificationData.verified_details.exit_type || ''} 
                onValueChange={(v) => handleFieldVerification('exit_type', true, v)}
              >
                <SelectTrigger className="h-8 text-sm border-slate-300"><SelectValue placeholder="Select exit type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="resignation"><span className="text-green-600">Resignation</span></SelectItem>
                  <SelectItem value="termination"><span className="text-red-600">Termination</span></SelectItem>
                  <SelectItem value="absconding"><span className="text-red-600">Absconding</span></SelectItem>
                  <SelectItem value="mutual_separation"><span className="text-green-600">Mutual Separation</span></SelectItem>
                  <SelectItem value="contract_end"><span className="text-blue-600">Contract End</span></SelectItem>
                  <SelectItem value="currently_employed"><span className="text-sky-600">Currently Employed</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.eligible_for_rehire === 'yes' 
                ? 'bg-green-50 border-green-200' 
                : verificationData.verified_details.eligible_for_rehire === 'no'
                  ? 'bg-red-50 border-red-200'
                  : 'bg-white border-indigo-100'
            }`}>
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <CheckCircle className="w-3 h-3 text-indigo-500" />Eligible for Rehire
              </Label>
              <Select 
                value={verificationData.verified_details.eligible_for_rehire || ''} 
                onValueChange={(v) => handleFieldVerification('eligible_for_rehire', true, v)}
              >
                <SelectTrigger className="h-8 text-sm border-slate-300"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="yes"><span className="text-green-600 font-medium">✓ Yes</span></SelectItem>
                  <SelectItem value="no"><span className="text-red-600 font-medium">✗ No</span></SelectItem>
                  <SelectItem value="not_disclosed"><span className="text-amber-600">Not Disclosed</span></SelectItem>
                  <SelectItem value="with_conditions"><span className="text-blue-600">Yes, with conditions</span></SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="p-3 bg-white rounded-lg border-2 border-indigo-100 shadow-sm">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1 mb-2">
                <Calendar className="w-3 h-3 text-indigo-500" />Verification Date
              </Label>
              <Input 
                type="date"
                value={verificationData.verified_details.verification_date || new Date().toISOString().split('T')[0]} 
                onChange={(e) => handleFieldVerification('verification_date', true, e.target.value)}
                className="h-8 text-sm border-indigo-200"
              />
            </div>
          </CardContent>
        </Card>
        
        {/* Attendance & Conduct Section */}
        <Card className="border-2 border-amber-200 shadow-md">
          <CardHeader className="pb-2 bg-gradient-to-r from-amber-500 to-orange-400 text-white rounded-t-lg">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Attendance & Conduct Verification
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4 pt-4 bg-gradient-to-b from-amber-50 to-white">
            {/* Attendance and Punctuality */}
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.attendance_rating === 'excellent' || verificationData.verified_details.attendance_rating === 'good'
                ? 'bg-green-50 border-green-200'
                : verificationData.verified_details.attendance_rating === 'poor'
                  ? 'bg-red-50 border-red-200'
                  : verificationData.verified_details.attendance_rating === 'average'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-white border-amber-100'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <Check className="w-3 h-3 text-amber-500" />
                  Attendance & Punctuality
                </Label>
                <Select 
                  value={verificationData.verified_details.attendance_status || ''} 
                  onValueChange={(v) => handleFieldVerification('attendance_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-36 text-xs border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    {verificationStatusOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                          {o.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select 
                value={verificationData.verified_details.attendance_rating || ''} 
                onValueChange={(v) => handleFieldVerification('attendance_rating', true, v)}
              >
                <SelectTrigger className="h-8 text-xs mb-2 border-slate-300"><SelectValue placeholder="Select rating" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="excellent"><span className="text-green-600 font-medium">✓ Excellent - No attendance issues</span></SelectItem>
                  <SelectItem value="good"><span className="text-green-600">Good - Minor issues only</span></SelectItem>
                  <SelectItem value="average"><span className="text-amber-600">Average - Some attendance concerns</span></SelectItem>
                  <SelectItem value="poor"><span className="text-red-600 font-medium">✗ Poor - Frequent attendance issues</span></SelectItem>
                  <SelectItem value="not_disclosed"><span className="text-slate-500">Not Disclosed</span></SelectItem>
                </SelectContent>
              </Select>
              <Textarea 
                value={verificationData.verified_details.attendance_remarks || ''} 
                onChange={(e) => handleFieldVerification('attendance_remarks', true, e.target.value)}
                placeholder="Attendance & punctuality remarks (if any)..."
                className="h-16 text-xs resize-none border-slate-200"
              />
            </div>
            
            {/* Disciplinary Issues */}
            <div className={`p-3 rounded-lg border-2 shadow-sm transition-all ${
              verificationData.verified_details.disciplinary_record === 'none'
                ? 'bg-green-50 border-green-200'
                : verificationData.verified_details.disciplinary_record === 'termination_cause' || verificationData.verified_details.disciplinary_record === 'suspension'
                  ? 'bg-red-50 border-red-200'
                  : verificationData.verified_details.disciplinary_record === 'verbal_warning' || verificationData.verified_details.disciplinary_record === 'written_warning'
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-white border-amber-100'
            }`}>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3 text-amber-500" />
                  Any Disciplinary Issues
                </Label>
                <Select 
                  value={verificationData.verified_details.disciplinary_status || ''} 
                  onValueChange={(v) => handleFieldVerification('disciplinary_status', true, v)}
                >
                  <SelectTrigger className="h-7 w-36 text-xs border-slate-300"><SelectValue placeholder="Status" /></SelectTrigger>
                  <SelectContent>
                    {verificationStatusOptions.map(o => (
                      <SelectItem key={o.value} value={o.value}>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${o.color}`}></div>
                          {o.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Select 
                value={verificationData.verified_details.disciplinary_record || ''} 
                onValueChange={(v) => handleFieldVerification('disciplinary_record', true, v)}
              >
                <SelectTrigger className="h-8 text-xs mb-2 border-slate-300"><SelectValue placeholder="Select record status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none"><span className="text-green-600 font-medium">✓ No disciplinary issues on record</span></SelectItem>
                  <SelectItem value="verbal_warning"><span className="text-amber-600">Verbal Warning(s) on record</span></SelectItem>
                  <SelectItem value="written_warning"><span className="text-amber-600">Written Warning(s) on record</span></SelectItem>
                  <SelectItem value="suspension"><span className="text-red-600">Suspension on record</span></SelectItem>
                  <SelectItem value="termination_cause"><span className="text-red-600 font-medium">✗ Terminated for cause</span></SelectItem>
                  <SelectItem value="not_disclosed"><span className="text-slate-500">Not Disclosed</span></SelectItem>
                </SelectContent>
              </Select>
              <Textarea 
                value={verificationData.verified_details.disciplinary_remarks || ''} 
                onChange={(e) => handleFieldVerification('disciplinary_remarks', true, e.target.value)}
                placeholder="Disciplinary details (if any)..."
                className="h-16 text-xs resize-none border-slate-200"
              />
            </div>
          </CardContent>
        </Card>
      </div>
    );
}
